import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, CheckCircle, PieChart, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayoutOptimized';

interface Stats {
  totalLeads: number;
  pagesViewed: number;
  quizCompleted: number;
  rdvTaken: number;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats>({
    totalLeads: 0,
    pagesViewed: 0,
    quizCompleted: 0,
    rdvTaken: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      // Un seul log avant la requête
      
      // Style plus proche de l'original avec seulement un peu de logging
      const { data: prospects, error } = await supabase.from('prospects').select('*');
      
      if (error) {
        console.error('Erreur lors de la récupération des prospects:', error);
        return;
      }
      
      // Log minimal du résultat
      
      if (prospects) {
        setStats({
          totalLeads: prospects.length,
          pagesViewed: prospects.filter(p => p.page_vue).length,
          quizCompleted: prospects.filter(p => p.quiz_fait).length,
          rdvTaken: prospects.filter(p => p.rdv_pris).length,
        });
      }
    };

    fetchStats();
  }, []);

  const chartData = [
    { 
      name: 'Pages vues', 
      value: stats.totalLeads > 0 ? (stats.pagesViewed / stats.totalLeads) * 100 : 0 
    },
    { 
      name: 'Quiz complétés', 
      value: stats.totalLeads > 0 ? (stats.quizCompleted / stats.totalLeads) * 100 : 0 
    },
    { 
      name: 'RDV pris', 
      value: stats.totalLeads > 0 ? (stats.rdvTaken / stats.totalLeads) * 100 : 0 
    },
  ];

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <Link
              to="/mon-espace/prospects"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Voir tous les prospects
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Leads</p>
                  <p className="text-2xl font-semibold">{stats.totalLeads}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Pages vues</p>
                  <p className="text-2xl font-semibold">{stats.pagesViewed}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <PieChart className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Quiz complétés</p>
                  <p className="text-2xl font-semibold">{stats.quizCompleted}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">RDV pris</p>
                  <p className="text-2xl font-semibold">{stats.rdvTaken}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow mb-8">
            <h2 className="text-xl font-semibold mb-4">Conversion</h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis unit="%" />
                  <Tooltip 
                    formatter={(value) => {
                      // S'assurer que la valeur est un nombre valide
                      const numValue = Number(value);
                      return isNaN(numValue) ? '0%' : `${numValue.toFixed(1)}%`;
                    }}
                  />
                  <Bar dataKey="value" fill="#4F46E5" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
